import { useNavigate } from "react-router-dom";
import { useSeriesList } from "@/hooks/useSeries";
import { Sidebar } from "@/components/Sidebar";
import { useState } from "react";
import { cn } from "@/lib/utils";
import { Loader2, BookOpen } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { useToast } from "@/hooks/use-toast";
import { SeriesCard } from "@/components/SeriesCard";
import { useMembershipStatus } from "@/hooks/useMembershipStatus";
import { useTrialSeries } from "@/hooks/useTrialSeries";
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from "@/components/ui/pagination";

const PAGE_SIZE = 12;

export default function SeriesList() {
  const [sidebarCollapsed, setSidebarCollapsed] = useState(true);
  const { data: seriesList = [], isLoading } = useSeriesList();
  const [currentPage, setCurrentPage] = useState(1);
  const { user, signOut } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();
  const { data: membership } = useMembershipStatus(user?.id);
  const { data: trial } = useTrialSeries();

  const visibleSeriesList =
    membership?.isAdmin
      ? seriesList
      : (membership?.isPremium || !trial?.trialSeriesId
          ? seriesList.filter((s: any) => !s.is_hidden)
          : seriesList.filter((s: any) => !s.is_hidden && s.id === trial.trialSeriesId));

  const handleLogout = async () => {
    await signOut();
    toast({ title: "已退出登录" });
  };

  const totalPages = Math.ceil(visibleSeriesList.length / PAGE_SIZE);
  const currentSeries = visibleSeriesList.slice(
    (currentPage - 1) * PAGE_SIZE,
    currentPage * PAGE_SIZE
  );

  return (
    <div className="min-h-screen bg-background">
      <Sidebar
        isCollapsed={sidebarCollapsed}
        onToggle={() => setSidebarCollapsed(!sidebarCollapsed)}
        currentPage="series"
        onNavigate={(p) => p === "home" ? navigate("/") : navigate(`/${p}`)}
        isLoggedIn={!!user}
        onLogout={handleLogout}
      />

      <main
        className={cn(
          "transition-all duration-300",
          "lg:ml-16",
          !sidebarCollapsed && "lg:ml-60"
        )}
      >
        <div className="px-3 py-4 lg:px-4 lg:py-6 max-w-[1920px] mx-auto">
          <div className="flex items-center justify-between mb-8">
            <div>
              <h1 className="text-2xl lg:text-3xl font-bold text-foreground mb-2">
                系列课程
              </h1>
              <p className="text-muted-foreground">
                系统化的日语学习系列，包含名师讲解与专题训练
              </p>
            </div>
          </div>

          {isLoading ? (
            <div className="flex items-center justify-center py-20">
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
              <span className="ml-2 text-muted-foreground">加载中...</span>
            </div>
          ) : visibleSeriesList.length === 0 ? (
            <div className="text-center py-20 bg-muted/30 rounded-lg border border-dashed">
              <BookOpen className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
              <h3 className="text-lg font-medium text-foreground">暂无系列</h3>
              <p className="text-muted-foreground mt-1">敬请期待更多精彩内容</p>
            </div>
          ) : (
            <>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
                {currentSeries.map((series) => (
                  <SeriesCard 
                    key={series.id}
                    series={series}
                    onClick={() => navigate(`/series/${series.id}`)}
                  />
                ))}
              </div>

              {totalPages > 1 && (
                <div className="mt-8">
                  <Pagination>
                    <PaginationContent>
                      <PaginationItem>
                        <PaginationPrevious 
                          onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                          className={currentPage === 1 ? "pointer-events-none opacity-50" : "cursor-pointer"}
                        />
                      </PaginationItem>
                      
                      {Array.from({ length: totalPages }).map((_, i) => (
                        <PaginationItem key={i}>
                          <PaginationLink
                            isActive={currentPage === i + 1}
                            onClick={() => setCurrentPage(i + 1)}
                            className="cursor-pointer"
                          >
                            {i + 1}
                          </PaginationLink>
                        </PaginationItem>
                      ))}

                      <PaginationItem>
                        <PaginationNext
                          onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                          className={currentPage === totalPages ? "pointer-events-none opacity-50" : "cursor-pointer"}
                        />
                      </PaginationItem>
                    </PaginationContent>
                  </Pagination>
                </div>
              )}
            </>
          )}
        </div>
      </main>
    </div>
  );
}
